import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    completePlanDeveloppementStepRequest,
    createDemandeFormationFromPlanRequest,
    fetchPlanDeveloppementStepRequest,
    generatePlanDeveloppementRecommendationsRequest,
} from "../../../../redux/actions/performanceActions";
import DemandeFormModal from "../../../Formation/ResponsableFormation/RessourcesJs/demandes/DemandeFormModal";
import PlanDeveloppementCard from "../../components/PlanDeveloppementCard";
import {
    buildFormationCardFromItem,
    normalizeFormationForRequest,
    staticPlanCards,
} from "../../components/planDeveloppementCardUtils";
import { asArray, StepState } from "../../Collaborateur/performanceUi";
import "../../Collaborateur/steps/PlanDeveloppementStep.css";

export default function PlanDeveloppementManagerStep({ progressId }) {
    const dispatch = useDispatch();
    const slice = useSelector((state) => state.performance.planDeveloppement);
    const data = slice.stepByProgressId[String(progressId)];
    const loading = slice.loadingByProgressId[String(progressId)];
    const error = slice.errorByProgressId[String(progressId)];
    const recommendations = asArray(data?.recommendations);
    const items = asArray(data?.items);
    const hasFormationCampaign = data?.formationCampaignAssociated !== false;

    const [selectedPlanItem, setSelectedPlanItem] = useState(null);
    const [showDemandeModal, setShowDemandeModal] = useState(false);
    const [isSubmittingDemande, setIsSubmittingDemande] = useState(false);

    useEffect(() => {
        dispatch(fetchPlanDeveloppementStepRequest(progressId));
    }, [dispatch, progressId]);

    const cardsToDisplay = useMemo(
        () => (hasFormationCampaign ? items.map(buildFormationCardFromItem) : staticPlanCards),
        [hasFormationCampaign, items]
    );
    const displayedCards = useMemo(() => cardsToDisplay.slice(0, 3), [cardsToDisplay]);

    const handleCloseDemandeModal = () => {
        setShowDemandeModal(false);
        setSelectedPlanItem(null);
        setIsSubmittingDemande(false);
    };

    useEffect(() => {
        if (!isSubmittingDemande || loading) {
            return;
        }

        const submittedItemId = selectedPlanItem?.itemId;
        const submittedItem = submittedItemId
            ? items.find((item) => String(item.id) === String(submittedItemId))
            : null;

        if (error) {
            setIsSubmittingDemande(false);
            return;
        }

        if (submittedItemId && submittedItem?.demandeId) {
            handleCloseDemandeModal();
        }
    }, [error, isSubmittingDemande, items, loading, selectedPlanItem]);

    const handleOpenDemandeModal = (item) => {
        const normalizedFormation = normalizeFormationForRequest({
            ...item,
            title: item.nom || item.titre || item.title,
            category: item.category,
            duration: item.duration,
        });

        setSelectedPlanItem({
            ...item,
            formation: normalizedFormation,
        });
        setShowDemandeModal(true);
    };

    const handleSubmitDemande = (payloadFromModal) => {
        if (!selectedPlanItem?.itemId) {
            return;
        }

        setIsSubmittingDemande(true);
        dispatch(
            createDemandeFormationFromPlanRequest({
                evaluationProgressId: progressId,
                planDeveloppementItemId: selectedPlanItem.itemId,
                mode: payloadFromModal.mode,
                period: payloadFromModal.period,
                niveau: payloadFromModal.niveau,
                certifiante: payloadFromModal.certifiante,
                priorite: payloadFromModal.priorite,
                objectifs: payloadFromModal.objectifs,
                objectifs2: payloadFromModal.objectifs2,
                objectifs3: payloadFromModal.objectifs3,
                description: payloadFromModal.description,
            })
        );
    };

    return (
        <StepState loading={loading} error={error} empty={!data}>
            {!hasFormationCampaign ? (
                <div className="plan-container">
                    <h3 className="plan-section">Plan de developpement</h3>
                    <div className="plan-manager-grid">
                        {displayedCards.map((card) => (
                            <PlanDeveloppementCard
                                key={card.id}
                                item={card}
                                onDemander={handleOpenDemandeModal}
                                variant="manager"
                            />
                        ))}
                    </div>
                </div>
            ) : (
                <>
                    {data?.blockedReason && <div className="perf-state perf-state--warning">{data.blockedReason}</div>}
                    <div className="perf-actions perf-actions--top">
                        <button className="perf-btn" type="button" onClick={() => dispatch(generatePlanDeveloppementRecommendationsRequest(progressId))}>
                            Regenerer recommandations
                        </button>
                        <button className="perf-btn perf-btn--primary" type="button" onClick={() => dispatch(completePlanDeveloppementStepRequest(progressId))}>
                            Terminer l'etape
                        </button>
                    </div>
                    <h3 className="perf-section-title">Recommandations backend</h3>
                    {recommendations.length === 0 && <div className="perf-state">Aucune recommandation.</div>}
                    <div className="perf-stack">
                        {recommendations.map((recommendation, index) => (
                            <article className="perf-item" key={recommendation.competenceNom || recommendation.skillNom || index}>
                                <div className="perf-item__head">
                                    <h3>{recommendation.competenceNom || recommendation.skillNom || "Recommendation"}</h3>
                                    <span className="perf-badge perf-badge--red">Gap {recommendation.gap ?? "-"}</span>
                                </div>
                                <p>{recommendation.reason || "Recommandation proposee par le backend."}</p>
                            </article>
                        ))}
                    </div>
                    <h3 className="perf-section-title">Plan de developpement</h3>
                    {displayedCards.length === 0 && <div className="perf-state">Aucun element dans le plan.</div>}
                    <div className="plan-manager-grid">
                        {displayedCards.map((card) => (
                            <PlanDeveloppementCard
                                key={card.id}
                                item={card}
                                onDemander={handleOpenDemandeModal}
                                variant="manager"
                            />
                        ))}
                    </div>
                </>
            )}

            <DemandeFormModal
                open={showDemandeModal}
                onClose={handleCloseDemandeModal}
                onSubmit={handleSubmitDemande}
                formation={selectedPlanItem?.formation || null}
                campagneId={null}
                type="FORMATION"
                loading={isSubmittingDemande}
                requireCampagneSelection={false}
            />
        </StepState>
    );
}
